/**
 * Переиспользуемый компонент отображения доступных каналов пациента
 * Дата: 10.02.2026
 * 
 * Используется в:
 * - Patients.tsx — колонка "Каналы" в таблице пациентов (TODO-052)
 * - PhoneBinding.tsx — статус привязки TG/MAX
 * - Appointments.tsx — инфо о каналах пациента
 * - ChatWindow.tsx — заголовок чата (доступные каналы)
 * 
 * Связан с: components/ui/Badge.tsx, components/ui/index.ts (экспорт)
 * 
 * КАК РАБОТАЕТ:
 * - Принимает объект channels с флагами доступности каждого канала
 * - Рендерит бейджи с иконками и цветовой индикацией (✅ зелёный / ❌ серый)
 * - Опциональный шильдик ⚙️ для индивидуальных настроек (preferred_channel)
 * - Компактный режим (size='sm') для таблиц
 */
import { cn } from '@/utils/cn';
import { ChannelIcon } from '@/components/ui/ChannelIcon';

export interface ChannelAvailability {
  /** WhatsApp WABA */
  whatsapp_waba?: boolean;
  /** WhatsApp Regular */
  whatsapp_regular?: boolean;
  /** Telegram Bot */
  telegram_bot?: boolean;
  /** Telegram Client */
  telegram_client?: boolean;
  /** MAX (VK) */
  max?: boolean;
  /** MAX Client */
  max_client?: boolean;
  /** SMS */
  sms?: boolean;
  /** SMS-Tel (Android phone) */
  sms_tel?: boolean;
  /** Просто телефон (для WhatsApp/SMS) */
  phone?: boolean;
}

export interface ChannelBadgesProps {
  /** Доступность каналов */
  channels: ChannelAvailability;
  /** Есть ли индивидуальные настройки (preferred_channel) */
  hasCustomSettings?: boolean;
  /** Название preferred_channel для tooltip */
  preferredChannel?: string | null;
  /** Размер: sm для таблиц, md для карточек */
  size?: 'sm' | 'md';
  /** Какие каналы показывать (по умолчанию все с true/false) */
  showChannels?: (keyof ChannelAvailability)[];
  /** Показывать только доступные каналы */
  showOnlyAvailable?: boolean;
  /** Дополнительные CSS классы */
  className?: string;
  /** data-testid */
  testId?: string;
}

/** Конфиг канала для отображения */
const CHANNEL_CONFIG: Record<string, { label: string; icon: string; shortLabel: string }> = {
  phone: { label: 'Телефон', icon: '📱', shortLabel: 'Тел' },
  whatsapp_waba: { label: 'WhatsApp WABA', icon: '💬', shortLabel: 'WABA' },
  whatsapp_regular: { label: 'WhatsApp Reg', icon: '💬', shortLabel: 'Reg' },
  telegram_bot: { label: 'Telegram Bot', icon: '🤖', shortLabel: 'TG Bot' },
  telegram_client: { label: 'Telegram Client', icon: '✈️', shortLabel: 'TG Cli' },
  max: { label: 'MAX (VK)', icon: '💜', shortLabel: 'MAX' },
  max_client: { label: 'MAX Client', icon: '💜', shortLabel: 'Max Cli' },
  sms: { label: 'SMS', icon: '📩', shortLabel: 'SMS' },
  sms_tel: { label: 'SMS-Tel', icon: '📲', shortLabel: 'SMS-Tel' },
};

/** Порядок отображения каналов */
const DEFAULT_ORDER: (keyof ChannelAvailability)[] = [
  'phone', 'whatsapp_waba', 'whatsapp_regular', 
  'telegram_bot', 'telegram_client', 'max', 'max_client', 'sms', 'sms_tel',
];

/** Дружественное имя preferred_channel */
const PREFERRED_CHANNEL_NAMES: Record<string, string> = {
  whatsapp_waba: 'WhatsApp WABA',
  whatsapp_regular: 'WhatsApp',
  telegram_bot: 'Telegram Bot',
  telegram_client: 'Telegram Client',
  max: 'MAX',
  max_client: 'MAX Client',
  sms: 'SMS',
  sms_tel: 'SMS-Tel',
};

function getChannelTitle(channelKey: keyof ChannelAvailability, isAvailable: boolean): string {
  if (!isAvailable) {
    return `Канал недоступен: ${CHANNEL_CONFIG[channelKey]?.label || channelKey}`;
  }

  switch (channelKey) {
    case 'telegram_bot':
      return 'У пациента доступен TG Bot';
    case 'telegram_client':
      return 'У пациента доступен Telegram Client';
    case 'max':
      return 'У пациента доступен MAX';
    case 'max_client':
      return 'У пациента доступен MAX Client';
    case 'sms':
      return 'Есть телефон, можно отправить SMS';
    case 'whatsapp_waba':
      return 'Есть телефон, можно отправить в WhatsApp WABA';
    case 'whatsapp_regular':
      return 'Есть телефон, можно отправить в WhatsApp Regular';
    case 'phone':
      return 'У пациента есть телефон';
    default:
      return `Канал пациента: ${CHANNEL_CONFIG[channelKey]?.label || channelKey}`;
  }
}

function getChannelBadgeClass(channelKey: keyof ChannelAvailability, isAvailable: boolean): string {
  if (!isAvailable) {
    return 'bg-neutral-50 text-neutral-400 border border-neutral-200';
  }

  switch (channelKey) {
    case 'telegram_bot':
      return 'bg-blue-50 text-blue-700 border border-blue-200';
    case 'telegram_client':
      return 'bg-cyan-50 text-cyan-700 border border-cyan-200';
    case 'max':
      return 'bg-violet-50 text-violet-700 border border-violet-200';
    case 'max_client':
      return 'bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200';
    case 'sms':
      return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'whatsapp_waba':
      return 'bg-green-50 text-green-700 border border-green-200';
    case 'whatsapp_regular':
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    default:
      return 'bg-slate-50 text-slate-700 border border-slate-200';
  }
}

/**
 * ChannelBadges — визуальное отображение доступных каналов пациента
 * 
 * @example
 * // В таблице пациентов
 * <ChannelBadges 
 *   channels={{ phone: true, whatsapp_waba: true, telegram_bot: true, max: false }}
 *   hasCustomSettings={!!patient.preferred_channel}
 *   size="sm"
 * />
 * 
 * @example
 * // Только доступные
 * <ChannelBadges 
 *   channels={patient.available_channels}
 *   showOnlyAvailable
 * />
 */
export function ChannelBadges({
  channels,
  hasCustomSettings = false,
  preferredChannel,
  size = 'sm',
  showChannels,
  showOnlyAvailable = false,
  className,
  testId,
}: ChannelBadgesProps) {
  const channelsToShow = showChannels || DEFAULT_ORDER;

  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-xs gap-0.5',
    md: 'px-2 py-1 text-sm gap-1',
  };

  return (
    <div className={cn('flex flex-wrap gap-1 items-center', className)} data-testid={testId}>
      {channelsToShow.map((channelKey) => {
        const isAvailable = channels[channelKey] ?? false;
        const config = CHANNEL_CONFIG[channelKey];
        if (!config) return null;
        if (showOnlyAvailable && !isAvailable) return null;

        return (
          <span
            key={channelKey}
            title={getChannelTitle(channelKey, isAvailable)}
            className={cn(
              'inline-flex items-center rounded-md font-medium transition-colors',
              sizeClasses[size],
              getChannelBadgeClass(channelKey, isAvailable),
            )}
          >
            <ChannelIcon channelId={channelKey} size={size === 'sm' ? 14 : 16} />
            <span>{config.shortLabel}</span>
          </span>
        );
      })}

      {/* Шильдик индивидуальных настроек */}
      {hasCustomSettings && (
        <span
          title={
            preferredChannel
              ? `Индивидуальные настройки: ${PREFERRED_CHANNEL_NAMES[preferredChannel] || preferredChannel}`
              : 'Индивидуальные настройки уведомлений'
          }
          className={cn(
            'inline-flex items-center rounded-md font-medium',
            sizeClasses[size],
            'bg-amber-50 text-amber-700 border border-amber-200 cursor-help',
          )}
        >
          ⚙️
        </span>
      )}
    </div>
  );
}
